import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceArea
} from 'recharts';
import _ from 'lodash';

const Co2Chart = ({ data }) => {
  const processSensorData = (sensorData) => {
    const processedData = [];
    Object.entries(sensorData).forEach(([timestamp, readings]) => {
      // Split timestamp into date and time parts
      const [datePart, timePart] = timestamp.split(' ');
      const [year, month, day] = datePart.split('-');
      
      processedData.push({
        timestamp: timestamp,
        datetime: `${day}.${month} ${timePart}`,
        value: readings.value,
        threshold: readings.threshold
      });
    });
    return _.sortBy(processedData, 'timestamp');
  };

  if (!data?.co2) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.entries(data.co2).map(([sensorName, sensorData]) => {
        const chartData = processSensorData(sensorData);
        
        return (
          <div key={sensorName} className="bg-white rounded-lg p-2">
            <div className="text-sm font-medium mb-2">{sensorName}</div>
            <ResponsiveContainer width="100%" height={100}>
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="datetime"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(tickItem) => {
                    if (!tickItem) return '';
                    
                    const parts = tickItem.split(' ');
                    if (parts.length < 2) return tickItem;
                    
                    const timePart = parts[1];
                    
                    if (timePart === '00:00') {
                      return parts[0]; // Return the date part for midnight
                    }
                    
                    return timePart; // Return just the hour for other times
                  }}
                  ticks={(() => {
                    // Create one tick per day at midnight
                    const ticks = [];
                    const seenDates = new Set();
                    
                    chartData.forEach(dataPoint => {
                      if (!dataPoint.datetime) return;
                      
                      const [datePart] = dataPoint.datetime.split(' ');
                      
                      if (!seenDates.has(datePart)) {
                        seenDates.add(datePart);
                        // Find the midnight entry for this date if it exists
                        const midnightEntry = chartData.find(d => 
                          d.datetime.startsWith(datePart) && d.datetime.includes('00:00')
                        );
                        
                        if (midnightEntry) {
                          ticks.push(midnightEntry.datetime);
                        } else {
                          // If no midnight entry, use the first entry for this date
                          const firstEntry = chartData.find(d => 
                            d.datetime.startsWith(datePart)
                          );
                          if (firstEntry) {
                            ticks.push(firstEntry.datetime);
                          }
                        }
                      }
                    });
                    
                    return ticks;
                  })()}
                />
                <YAxis
                  domain={[400, 1200]}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => Math.round(value)}
                  label={{
                    value: 'ppm',
                    position: 'insideLeft',
                    angle: -90,
                    dx: 10,
                    fontSize: 10
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'threshold')
                      return [`${Math.round(value)} ppm`, 'Threshold'];
                    return [`${Math.round(value)} ppm`, 'CO2'];
                  }}
                />

                {/* Green background for area below threshold */}
                <ReferenceArea
                  y1={400}  // YAxis minimum
                  y2={chartData[0]?.threshold}
                  fill="#4caf50"
                  fillOpacity={0.1}
                />

                {/* Simple CO2 value line */}
                <Line
                  type="monotone"
                  dataKey="value"
                  name="CO2"
                  dot={false}
                  stroke="#1976d2"  // Using a nice blue color
                />
                
                {/* Threshold line */}
                <Line
                  type="monotone"
                  dataKey="threshold"
                  name="Threshold"
                  dot={false}
                  stroke="#ff0000"
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
};

export default Co2Chart;
